/**
 * Live updates for the door: one socket, joined to the event's room by the server (an event-bound kiosk token gets its own event
 * only; a planner's session gets the project and we ignore other events here). The server announces `door:changed`
 * { event_id, kind, guest, at }. The socket is a speed-up, never the only path: useDoor keeps polling and polls fast when this is down.
 */
import { io, type Socket } from 'socket.io-client';
import { getApiKey, getApiUrl } from './PlanningApiClient';
import type { DoorCredentials } from './DoorApiClient';
import type { DoorFavour, DoorGuest } from '../types/Door';

export interface DoorChange {
  event_id: string;
  kind: 'check_in' | 'undo' | 'walk_in' | 'favour_handed' | 'favour_returned';
  guest: DoorGuest;
  favour?: DoorFavour;
  at: string;
}

export interface DoorSocketHandlers {
  onChange: (change: DoorChange) => void;
  onConnection: (connected: boolean) => void;
}

type IoFactory = (url: string, opts: Record<string, unknown>) => Pick<Socket, 'on' | 'disconnect'>;

/** Connects and returns a function that closes it. Reconnects by itself with a growing delay (socket.io default). */
export function connectDoorSocket(
  credentials: DoorCredentials, eventId: string, handlers: DoorSocketHandlers, factory: IoFactory = io as unknown as IoFactory,
): () => void {
  const socket = factory(getApiUrl(), {
    auth: { token: credentials.token, api_key: getApiKey() },
    reconnection: true,
    reconnectionDelayMax: 10_000,
  });
  socket.on('connect', () => handlers.onConnection(true));
  socket.on('disconnect', () => handlers.onConnection(false));
  socket.on('connect_error', () => handlers.onConnection(false));
  socket.on('door:changed', (change: DoorChange) => {
    if (change && change.event_id === eventId && change.guest && typeof change.guest.id === 'string') handlers.onChange(change);
  });
  return () => { socket.disconnect(); };
}
